/* eslint-disable complexity */
/* eslint-disable max-statements */
import React, {useState, useCallback, useRef, useEffect} from "react";
import PropTypes from "prop-types";
import styles from "./styles.css";
import { debounce } from "lodash";
import { googlePlacesOptions } from "../../../utils/auto-complete/search-options";
import { depriotisePublicLocations, isLocationFeasibleForInspection } from "../../../utils/helpers/check-location-feasibility";
import { LOCATION_REQUEST_TYPE, MAP_TYPE, NUMBER } from "../../../constants/app-constants";
import { userLocator } from "../../../utils/auto-complete/user-locator";
import AutoComplete from "../../../utils/auto-complete";
import { TAB_LIST } from "../location-picker-with-map-v2/constant";
import SpecificLocation from "./images/specific-location.svg";
import { sendCustomGaEvents } from "../../../tracking";
import { locationTypeSelection, locationAccess, locationMethodSelection } from  "./tracking";
import DisableLocation from "./images/location-icon.svg";
import LocationIcon from "./images/gps-fixed.svg";
import { BodyText } from "@cars24/turbo-web/lib/body-text";
import { Divider } from "@cars24/turbo-web/lib/divider";
import { SearchInput } from "@cars24/turbo-web/lib/search-input";
import { Button } from "@cars24/turbo-web/lib/button";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import { Label } from "@cars24/turbo-web/lib/label";
import { Chips } from "@cars24/turbo-web/lib/chips";

const InspectionLocationSearchV2 = ({
    isLocationSearchEnabled,
    inspectionLocation,
    position,
    onLocationChange,
    // goBack,
    getPincode,
    token = "",
    setInspectionLocation,
    setIsHiAvailable,
    handleLocationAccessGaEvent = () => {},
    setIsLocationSearchEnabled,
    leadDetails
}) => {
    const {tokens} = useTokens();
    const mapType = MAP_TYPE.GOOGLE;
    const coordinates = (leadDetails && leadDetails.lat) ? { lat: leadDetails.lat, lng: leadDetails.lng } : position;
    const locationBounds =  {
        location: new window.google.maps.LatLng(coordinates.lat, coordinates.lng), radius: 10000
    };
    const inputRef = useRef(null);
    // const [isFetching, setIsFetching] = useState(false);
    const [searchedPlaces, setSearchedPlaces] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [locationError, setLocationError] = useState(false);
    const [isLocationOutOfBounds, setIsLocationOutOfBounds] = useState(false);

    useEffect(() => {
        if (isLocationSearchEnabled && !inspectionLocation) {
            setInspectionLocation(TAB_LIST[NUMBER.TWO]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLocationSearchEnabled]);

    useEffect(() => {
        if (isLocationSearchEnabled && inputRef && inputRef.current) {
            window.setTimeout(() => {
                inputRef.current.focus();
            }, NUMBER.FIVE_HUNDRED);
        }
    }, [isLocationSearchEnabled, inputRef]);

    const handlePlaceList = (input, list) => {
        const filteredList  = depriotisePublicLocations(input, list);
        setSearchedPlaces(filteredList);
    };

    const delayedQuery = useCallback(
        debounce((val) =>
            googlePlacesOptions(val, mapType, locationBounds)
                .then(resp => handlePlaceList(val, resp))
        , NUMBER.FIVE_HUNDRED),
        []
    );

    const handleLocationChange = (value, locationInfo = "") => {
        setInputValue(value);
        if (value && value.length > NUMBER.THREE) {
            delayedQuery(`${locationInfo}${value}`);
        } else {
            setSearchedPlaces(null);
        }
    };

    const handleLocationType = (item) => {
        sendCustomGaEvents({
            ...locationTypeSelection,
            eventAction: item.title,
            eventLabel: token
        });
        setInspectionLocation(item);
    };

    const handleLocationSearch = (flag) => {
        setIsLocationSearchEnabled(flag);
        sendCustomGaEvents({
            ...locationMethodSelection,
            eventAction: "search_used",
            eventLabel: token
        });
    };

    const onChangeHandler = ({ target: { value } }) => {
        if (!value) {
            setSearchedPlaces(null);
            setIsLocationOutOfBounds(false);
        }
        handleLocationChange(value);
    };

    const handleResetVal = () => {
        setInputValue("");
        setSearchedPlaces(null);
    };

    const proceedWithMapView = ({coords, place, location}) => {
        const { label, formattedAddress } = place;
        const locationObj = {
            coords,
            label,
            formattedAddress,
            pincode: getPincode(location[0])
        };

        onLocationChange(locationObj);
    };

    const handleUserJourney = ({coords, place, location, isAutoDetect = false}) => {
        if (isAutoDetect) {
            onLocationChange({coords, isAutoDetect: true});
        } else {
            proceedWithMapView({coords, place, location});
        }
    };

    const detectUserLocation = () => {
        if (!locationError) {
            setIsHiAvailable(false);
            if (!isLocationSearchEnabled) {
                sendCustomGaEvents({
                    ...locationMethodSelection,
                    eventAction: "current_location",
                    eventLabel: token
                });
            }
            // setIsFetching(true);
            userLocator((coords) => {
                handleLocationAccessGaEvent(true);
                setLocationError(false);
                handleUserJourney({coords, isAutoDetect: true});
                sendCustomGaEvents({
                    ...locationAccess,
                    eventAction: `location_access_on`,
                    eventLabel: token
                });
            }, () => {
                handleLocationAccessGaEvent(false);
                setLocationError(true);
                // setIsFetching(false);
                handleLocationSearch(true);
                sendCustomGaEvents({
                    ...locationAccess,
                    eventAction: `location_access_off`,
                    eventLabel: token
                });
            }, true);
        }
    };

    const handleOnPlaceSelect = async (place) => {
        setIsHiAvailable(false);
        try {
            const { value } = place;
            // setIsFetching(true);
            const response = await AutoComplete.getInstance(mapType).getGeoCode({
                placeId: value
            }, LOCATION_REQUEST_TYPE.PLACE);

            const coords = response[0].geometry.location;
            const locationCoordinates = { lat: coords.lat(), lng: coords.lng() };
            if (isLocationFeasibleForInspection(response[0])) {
                handleUserJourney({coords: locationCoordinates, place, location: response});
            } else {
                setIsLocationOutOfBounds(true);
                handleResetVal();
                // setIsFetching(false);
                sendCustomGaEvents({
                    event: "slot_availabiliy",
                    eventCategory: `location_method_Search`,
                    eventAction: "null",
                    eventLabel: token
                });
            }
        } catch (error) {
            // setIsFetching(false);
        }
    };

    // const goBackHandler = () => {
    //     setIsLocationSearchEnabled(false);
    // };

    const renderHelpSection = () => {
        return (
            <div styleName={"styles.helpusLocation"}>
                <Label
                    level={NUMBER.TWO}
                    color={tokens.text_default_text_primary}
                    fontWeight="semibold"
                >
                        Help us with your accurate location
                </Label>
                {
                    <div styleName={"styles.selectLocationCta"}>
                        <Button
                            onClick={detectUserLocation}
                            disabled={locationError}
                            label={!locationError ? "Use Current Location" : "Location access denied"}
                            prefixIcon={<img src={locationError ? DisableLocation : LocationIcon} alt="location" />}
                        />
                        <div styleName={"styles.locationText"}>
                            <BodyText
                                level={NUMBER.THREE}
                                color={tokens.text_default_text_secondary}
                            >
                                {inspectionLocation.description}
                                <img src={inspectionLocation.icon} alt="" />
                            </BodyText>
                        </div>
                    </div>
                }
                <div styleName={"styles.divider"}>
                    <Divider variant="tertiary" />
                </div>
                <SearchInput
                    placeholder={inspectionLocation.placeholder}
                    onClick={() => handleLocationSearch(true)}
                />
            </div>
        );
    };

    const renderHeader = () => {
        return (
            <BodyText level={NUMBER.ONE} color={tokens.text_default_text_secondary}>Our car expert will come here for Inspection</BodyText>
        );
    };

    if (isLocationSearchEnabled) {
        return (
            <React.Fragment>
                {renderHeader()}
                <div styleName={"styles.divider"}>
                    <Divider variant="secondary" />
                </div>
                <div>
                    <div>
                        <Label
                            level={NUMBER.TWO}
                            color={tokens.text_default_text_primary}
                            fontWeight="semibold"
                        >
                            Help us with your accurate location
                        </Label>
                        <div styleName={"styles.selectLocationCta"}>
                            <Button
                                onClick={detectUserLocation}
                                disabled={locationError}
                                label={!locationError ? "Use Current Location" : "Location access denied"}
                                prefixIcon={<img src={locationError ? DisableLocation : LocationIcon} alt="location" />}
                            />
                        </div>
                        <div styleName={"styles.divider"}>
                            <Divider variant="tertiary" />
                        </div>
                        <SearchInput
                            isAutoFocus={isLocationSearchEnabled}
                            placeholder={inspectionLocation.placeholder}
                            onChange={onChangeHandler}
                            value={inputValue}
                            showCross={false}
                            maxLength={255}
                        />
                        {
                            (!locationError || isLocationOutOfBounds) &&
                            <div>
                                {
                                    isLocationOutOfBounds && !inputValue &&
                                    <div styleName={"styles.locationBounds"}>
                                        <h5>Please be more specific</h5>
                                        <span>E.g: Tower B, Vijay Apartment</span>
                                        <img src={SpecificLocation} alt="specific location" width="148px"  height="160px"/>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                    <ul styleName={"styles.locationList"}>
                        {
                            searchedPlaces && searchedPlaces.map((place, index) => {
                                return (
                                    <li key={index} onClick={() => handleOnPlaceSelect(place)}>
                                        <Label
                                            level={NUMBER.THREE}
                                            color={tokens.text_default_text_primary}
                                            fontWeight={"semibold"}
                                        >
                                            {place.formattedAddress.main_text}
                                        </Label>
                                        <Label
                                            level={NUMBER.FOUR}
                                            color={tokens.text_default_text_secondary}
                                        >
                                            {place.formattedAddress.secondary_text}
                                        </Label>
                                    </li>
                                );
                            })
                        }
                    </ul>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {renderHeader()}
            <div styleName={"styles.divider"}>
                <Divider variant="secondary" />
            </div>
            <div>
                <Label
                    level={NUMBER.TWO}
                    color={tokens.text_default_text_primary}
                    fontWeight="semibold"
                >
                        Where should we inspect your car?
                </Label>
                <ul styleName={"styles.tabs"}>
                    {
                        TAB_LIST.map((item, index) => (
                            <Chips
                                key={`location-${index}`}
                                isActive={inspectionLocation && inspectionLocation.title === item.title}
                                onClick={() => {handleLocationType(item);}}
                                label={item.title}
                                prefixIcon={<img src={item.icon} alt={item.title} />}
                            />
                        ))
                    }
                </ul>
            </div>
            {inspectionLocation && !!Object.keys(inspectionLocation).length && renderHelpSection()}
        </React.Fragment>
    );
};

InspectionLocationSearchV2.propTypes = {
    isLocationSearchEnabled: PropTypes.bool,
    inspectionLocation: PropTypes.any,
    position: PropTypes.object,
    onLocationChange: PropTypes.func,
    // goBack: PropTypes.func,
    getPincode: PropTypes.func,
    showLocationErrorToast: PropTypes.bool,
    token: PropTypes.string,
    setInspectionLocation: PropTypes.func,
    setIsHiAvailable: PropTypes.func,
    handleLocationAccessGaEvent: PropTypes.func,
    setIsLocationSearchEnabled: PropTypes.func,
    leadDetails: PropTypes.object
};
export default InspectionLocationSearchV2;
